import { useCallback, useEffect } from 'react';
import { useStore } from 'react-redux';
import type { RootStateType } from 'src/app/store';
import {
  sendEditorMetric,
  sendLeftNoAction,
  type EditorMetricEvent,
  type EditorTip,
  type EditorTipAct,
} from 'src/utils/editorMetrics';
import { useIsDemoUser } from './useIsDemoUser';

/** Loaded suggestions of the routed document, as a count only. */
export const countLoadedSuggestions = (state: RootStateType): number =>
  state.editPage.pages.reduce((sum, page) => sum + (page?.length ?? 0), 0);

/** Demo users only; other users never send anything. */
export const useEditorMetrics = () => {
  const isDemo = useIsDemoUser();
  const store = useStore<RootStateType>();
  return useCallback(
    (event: EditorMetricEvent, pages?: number) => {
      if (!isDemo) return;
      const state = store.getState();
      sendEditorMetric(event, {
        pages,
        suggestions: countLoadedSuggestions(state),
        source: state.auth.demoSource,
      });
    },
    [isDemo, store]
  );
};

/**
 * `editor_tip` for demo users only: once per task view per tip/act pair, with
 * the in-memory demo source (no buckets).
 */
export const useEditorTipMetric = () => {
  const isDemo = useIsDemoUser();
  const store = useStore<RootStateType>();
  return useCallback(
    (tip: EditorTip, act: EditorTipAct) => {
      if (!isDemo) return;
      sendEditorMetric('editor_tip', {
        tip,
        act,
        source: store.getState().auth.demoSource,
      });
    },
    [isDemo, store]
  );
};

/**
 * `editor_left_no_action` for demo users: on pagehide, when results were shown
 * and there was no box edit, anonymise click or export. The dwell time is an
 * in-memory timer in editorMetrics.ts; nothing is stored. Under-counts (mobile
 * browsers and killed tabs may not fire pagehide): a trend only.
 */
export const useLeftNoActionMetric = () => {
  const isDemo = useIsDemoUser();
  const store = useStore<RootStateType>();
  useEffect(() => {
    if (!isDemo) return;
    const onPageHide = () => sendLeftNoAction(store.getState().auth.demoSource);
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [isDemo, store]);
};
